/**
 * Теги доски.
 *
 * §0.3 и §10: теги принадлежат КОНКРЕТНОЙ доске и управляются здесь.
 * Глобального справочника тегов нет и быть не должно.
 *
 * §10.2: системные статусные теги ОАП показываются отдельным списком и
 * не редактируются — их код неизменяем, а от них зависит отчётность.
 *
 * Прежде у «Добавить тег» и карандаша не было обработчиков, а архивация
 * показывала «подключается вместе с backend».
 */

import { ArchiveRestore } from "lucide-react";
import { useState } from "react";

import type { WsError } from "@taskflow/client-core";
import {
  Button,
  ConfirmDialog,
  ErrorState,
  FormField,
  IconButton,
  Input,
  Modal,
  SkeletonRows,
  StatusTag,
  Tag,
  toast,
} from "@/shared/ui";
import { Pencil, Plus, Trash2 } from "@/shared/ui/motionIcons";

import {
  type BoardTag,
  useArchiveTagMutation,
  useBoardTagsQuery,
  useCreateTagMutation,
  useRestoreTagMutation,
  useUpdateTagMutation,
} from "@taskflow/client-core/boards";

export function TagsSection({ boardId }: { boardId: string }) {
  const tags = useBoardTagsQuery(boardId, { skip: boardId === "" });
  const [archiveTag, { isLoading: archivingNow }] = useArchiveTagMutation();
  const [restoreTag] = useRestoreTagMutation();

  const [archiving, setArchiving] = useState<BoardTag | null>(null);
  const [editing, setEditing] = useState<BoardTag | "new" | null>(null);

  const regular = (tags.data?.data ?? []).filter((tag) => tag.kind === "REGULAR");
  const system = (tags.data?.data ?? []).filter((tag) => tag.kind === "SYSTEM_STATUS");

  return (
    <div className="flex flex-col gap-6">
      <section>
        <div className="mb-2.5 flex items-center justify-between">
          <div>
            <h2 className="text-[13px] font-extrabold text-fg">Теги доски</h2>
            <p className="text-[11px] text-faint">
              Принадлежат только этой доске — глобального справочника тегов нет
            </p>
          </div>
          <Button size="sm" variant="secondary" icon={Plus} onClick={() => setEditing("new")}>
            Добавить тег
          </Button>
        </div>

        {tags.isLoading && <SkeletonRows rows={2} />}

        {tags.error !== undefined && (
          <ErrorState
            message={(tags.error as WsError).data.message}
            onRetry={() => void tags.refetch()}
          />
        )}

        {tags.data !== undefined &&
          (regular.length === 0 ? (
            <p className="text-[13px] text-faint">Тегов пока нет</p>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {regular.map((tag) => (
                <li
                  key={tag.id}
                  className="flex items-center gap-3 rounded-md border border-line px-3 py-2"
                >
                  <Tag color={tag.color}>{tag.name}</Tag>
                  {tag.status === "ARCHIVED" && (
                    <span className="text-[11px] text-faint italic">
                      архивный — не предлагается новым задачам
                    </span>
                  )}
                  <span className="flex-1" />
                  <IconButton
                    icon={Pencil}
                    label={`Изменить тег ${tag.name}`}
                    size="sm"
                    onClick={() => setEditing(tag)}
                  />
                  {tag.status === "ARCHIVED" ? (
                    <IconButton
                      icon={ArchiveRestore}
                      label={`Восстановить тег ${tag.name}`}
                      size="sm"
                      onClick={() =>
                        void restoreTag({ boardId, tagId: tag.id })
                          .unwrap()
                          .then(() => toast.success("Тег восстановлен", tag.name))
                          .catch((raw: unknown) =>
                            toast.error(raw as WsError, "Не удалось восстановить тег"),
                          )
                      }
                    />
                  ) : (
                    <IconButton
                      icon={Trash2}
                      label={`Архивировать тег ${tag.name}`}
                      size="sm"
                      variant="danger"
                      onClick={() => setArchiving(tag)}
                    />
                  )}
                </li>
              ))}
            </ul>
          ))}
      </section>

      {system.length > 0 && (
        <section className="border-t border-line pt-5">
          <h2 className="mb-1 text-[13px] font-extrabold text-fg">Системные статусы</h2>
          {/* §10.2: не смешиваются с обычными тегами и не редактируются. */}
          <p className="mb-2.5 text-[11px] text-faint">
            Определяют статус задачи для отчётности. Коды неизменяемы, поэтому
            редактировать и удалять их нельзя.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {system.map((tag) => (
              <StatusTag key={tag.id} label={tag.name} code={tag.systemCode ?? ""} />
            ))}
          </div>
        </section>
      )}

      {editing !== null && (
        <TagDialog
          boardId={boardId}
          tag={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}

      <ConfirmDialog
        open={archiving !== null}
        title="Архивировать тег"
        description={
          archiving === null
            ? ""
            : `Тег «${archiving.name}» перестанет предлагаться новым задачам, но останется у тех, где уже проставлен.`
        }
        confirmLabel="Архивировать"
        loading={archivingNow}
        onConfirm={() => {
          if (archiving === null) return;
          void archiveTag({ boardId, tagId: archiving.id })
            .unwrap()
            .then(() => {
              toast.success("Тег архивирован", archiving.name);
              setArchiving(null);
            })
            .catch((raw: unknown) => toast.error(raw as WsError, "Не удалось архивировать тег"));
        }}
        onCancel={() => setArchiving(null)}
      />
    </div>
  );
}

function TagDialog({
  boardId,
  tag,
  onClose,
}: {
  boardId: string;
  tag: BoardTag | null;
  onClose: () => void;
}) {
  const [createTag, { isLoading: creating }] = useCreateTagMutation();
  const [updateTag, { isLoading: updating }] = useUpdateTagMutation();
  const [name, setName] = useState(tag?.name ?? "");
  const [color, setColor] = useState(tag?.color ?? "#6b7280");
  const [error, setError] = useState<string | null>(null);
  const busy = creating || updating;

  const submit = async () => {
    if (name.trim() === "") {
      setError("Укажите название тега");
      return;
    }
    setError(null);
    try {
      if (tag === null) {
        await createTag({ boardId, name: name.trim(), color }).unwrap();
        toast.success("Тег добавлен", name.trim());
      } else {
        await updateTag({ boardId, tagId: tag.id, version: tag.version, name: name.trim(), color }).unwrap();
        toast.success("Тег изменён", name.trim());
      }
      onClose();
    } catch (raw) {
      // Дубликат названия в пределах доски — содержательный отказ сервера.
      setError((raw as WsError).data.message);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={tag === null ? "Новый тег" : "Изменить тег"}
      width="sm"
      dismissable={!busy}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Отмена
          </Button>
          <Button loading={busy} onClick={() => void submit()}>
            {tag === null ? "Добавить" : "Сохранить"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3.5">
        <FormField label="Название" required error={error}>
          {(props) => (
            <Input {...props} value={name} autoFocus onChange={(event) => setName(event.target.value)} />
          )}
        </FormField>
        <FormField label="Цвет">
          {(props) => (
            <Input {...props} type="color" value={color} onChange={(event) => setColor(event.target.value)} />
          )}
        </FormField>
      </div>
    </Modal>
  );
}
